import { Button, Checkbox, Label, Spinner, Tree, TreeItem, TreeItemLayout, tokens } from '@fluentui/react-components';
import { ChevronDownFilled, ChevronRightFilled } from '@fluentui/react-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  getApiGraphFoldersOptions,
  postApiPermissionsApplyAllMutation,
  postApiPermissionsApplySelectedMutation,
} from '../../api/@tanstack/react-query.gen';
import { GetFoldersMsGraphResult } from '../../api/types.gen';
import { MailFolderItemMenu } from './MailFolderItemMenu';

export interface IMailFolderItemProps {
  selectable?: boolean;
  folder: GetFoldersMsGraphResult;
  checked?: boolean;
  onCheckBoxSelect?: (folder: string, state: boolean) => void;
  onSelectedFolder: (folder: string) => void;
  selectedFolderId: string | null | undefined;
}

export function MailFolderItem({
  folder,
  onCheckBoxSelect,
  checked,
  selectable,
  onSelectedFolder,
  selectedFolderId,
}: Readonly<IMailFolderItemProps>) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);

  const {
    data: mailFolders,
    isLoading: loading1,
    error: mailFoldersError,
  } = useQuery({
    ...getApiGraphFoldersOptions({ query: { ParentGraphFolderId: folder.graphFolderId ?? '' } }),
    enabled: open, // Only fetch children when the node is expanded
  });
  const applyPermissionsAll = useMutation({
    ...postApiPermissionsApplyAllMutation(),
  });
  const applyPermissionsSelect = useMutation({
    ...postApiPermissionsApplySelectedMutation(),
  });

  function handleApplyAll() {
    applyPermissionsAll.mutate({
      body: { graphFolderId: folder.graphFolderId!, exchangeFolderId: folder.exchangeFolderId },
    });
  }

  function handleApplySelected(selected: string[]) {
    applyPermissionsSelect.mutate({
      body: {
        graphFolderId: folder.graphFolderId!,
        exchangeFolderId: folder.exchangeFolderId,
        targetGraphFolderIds: selected ?? [],
      },
    });
  }
  const error = mailFoldersError || applyPermissionsAll.error || applyPermissionsSelect.error;
  const loading = loading1 || applyPermissionsAll.isPending || applyPermissionsSelect.isPending;
  // TODO isPedonging ist keine loading

  const isSelected = selectedFolderId === folder.graphFolderId;
  const isSelectable = isSelected || selectable;

  const handleCheckbox =
    selectable && onCheckBoxSelect
      ? onCheckBoxSelect
      : (folderId: string, state: boolean) => {
          if (state && folderId) {
            setSelectedFolders([...selectedFolders, folderId]);
          } else {
            setSelectedFolders([...selectedFolders.filter((x) => x != folderId)]);
          }
        };

  if (error) {
    console.error(error);
  }

  function clearSelectedFolders() {
    setSelectedFolders([]);
  }

  function handelChangeOpen(state: boolean) {
    if (state === false) {
      clearSelectedFolders();
    }
    setOpen(state);
  }

  const checkboxElement = selectable ? (
    <Checkbox
      checked={checked}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        onCheckBoxSelect?.(folder.graphFolderId!, e.target.checked);
      }}
    />
  ) : null;

  const mailFolderItems: GetFoldersMsGraphResult[] = mailFolders ?? [];

  return (
    <TreeItem itemType="leaf" open={open}>
      <TreeItemLayout
        style={isSelected ? { backgroundColor: tokens.colorNeutralBackground1Selected, cursor: 'pointer' } : { cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          if (!folder.graphFolderId) return;
          navigate({
            to: '/folder/$folderId',
            params: { folderId: folder.graphFolderId },
            search: { exchangeFolderId: folder.exchangeFolderId ?? undefined, folderName: folder.folderName ?? undefined },
          });
        }}
        iconBefore={
          folder.hasChildFolders ? (
            <>
              <Button
                appearance="transparent"
                icon={open ? <ChevronDownFilled /> : <ChevronRightFilled />}
                onClick={(e) => {
                  handelChangeOpen(!open);
                  e.stopPropagation();
                }}
              />
              {checkboxElement}
            </>
          ) : (
            <>
              {/* Invisible button to maintain alignment */}
              <Button icon={<ChevronDownFilled />} style={{ visibility: 'hidden', pointerEvents: 'none' }} />
              {checkboxElement}
            </>
          )
        }
        iconAfter={
          <>
            <MailFolderItemMenu
              display={isSelected ?? false}
              onClickApplyAll={handleApplyAll}
              onClickApplySelected={() => handleApplySelected(selectedFolders)}
            />
            {loading && open && <Spinner size="tiny" />}
          </>
        }
      >
        <Link
          style={{ textDecoration: 'none' }}
          to="/folder/$folderId"
          params={{
            folderId: folder.graphFolderId as string,
          }}
          search={{
            exchangeFolderId: folder.exchangeFolderId ?? undefined,
            folderName: folder.folderName ?? undefined,
          }}
        >
          <Label style={{ cursor: 'pointer' }} weight={isSelected ? 'semibold' : 'regular'}>
            {folder.folderName}
          </Label>
        </Link>
      </TreeItemLayout>
      {mailFolderItems.length > 0 && (
        <Tree>
          {mailFolderItems.map((e: GetFoldersMsGraphResult) => (
            <MailFolderItem
              key={e.graphFolderId}
              folder={e}
              selectedFolderId={selectedFolderId}
              selectable={isSelectable}
              onSelectedFolder={(f) => onSelectedFolder(f)}
              onCheckBoxSelect={handleCheckbox}
            />
          ))}
        </Tree>
      )}
    </TreeItem>
  );
}
